// ============================================================
// useChainIntegrity — OLIVIUM Integrity.
//
// Demo: chain is healthy by default; setDemoTamper(true) flips
//       it to invalid for the demo-only tamper toggle.
// Live: calls olv_verify_event_chain() RPC.
//       If the RPC is missing or errors, surfaces the message
//       "Awaiting chain verification service" and sets
//       missingRpc=true so views can render an honest banner.
//
// The hook is given the current event count from useAuditFeed
// so the "checked" number stays consistent across the views.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { getRuntime } from './_mode'
import type { ChainState } from './types'
import {
  captureHookFailure,
  recordHookOutcome,
  setChainContext,
  withSpan,
} from '../observability/sentry'

const INITIAL: ChainState = {
  loading: true,
  valid: true,
  checked: 0,
  message: '',
  missingRpc: false,
}

export function useChainIntegrity(eventCount: number) {
  const [state, setState] = useState<ChainState>(INITIAL)
  const [demoTamper, setDemoTamper] = useState(false)
  const [authRequired, setAuthRequired] = useState(false)

  const verify = useCallback(async () => {
    const { live, supabase } = getRuntime()

    if (!live || !supabase) {
      setAuthRequired(false)
      const next: ChainState = {
        loading: false,
        valid: !demoTamper,
        checked: eventCount,
        message: demoTamper ? 'Demo tamper simulation active.' : 'Demo chain verified.',
        missingRpc: false,
      }
      setState(next)
      setChainContext(next)
      recordHookOutcome('useChainIntegrity', 'demo_fallback', {
        message: next.message,
        rows: eventCount,
      })
      return
    }

    setState((p) => ({ ...p, loading: true }))
    setAuthRequired(false)

    const startedAt = Date.now()
    try {
      const { data: authData } = await supabase.auth.getSession()
      if (!authData.session) {
        const next: ChainState = {
          loading: false,
          valid: false,
          checked: eventCount,
          message: 'Sign in to verify chain integrity.',
          missingRpc: false,
        }
        setState(next)
        setChainContext(next)
        setAuthRequired(true)
        return
      }

      const { data, error } = await withSpan('olv_verify_event_chain', 'db.rpc', async () =>
        supabase.rpc('olv_verify_event_chain')
      )
      const durationMs = Date.now() - startedAt

      if (error) {
        // RPC absent or error — surface as missing dependency, never crash.
        const message = error.message || ''
        const missing =
          /function .*olv_verify_event_chain.* does not exist/i.test(message) ||
          /could not find the function/i.test(message) ||
          error.code === '42883'
        const next: ChainState = {
          loading: false,
          valid: false,
          checked: eventCount,
          message: 'Awaiting chain verification service',
          missingRpc: true,
        }
        setState(next)
        setChainContext(next)
        captureHookFailure(
          'useChainIntegrity',
          missing ? 'missing_dependency' : 'live_error',
          error,
          { rpc: 'olv_verify_event_chain', durationMs, message }
        )
        return
      }

      const payload = Array.isArray(data) ? data[0] : data
      const valid = Boolean(payload?.valid)
      const brokenAt = payload?.broken_at_sequence ?? payload?.broken_at
      const next: ChainState = {
        loading: false,
        valid,
        checked: Number(payload?.checked ?? payload?.checked_count ?? eventCount),
        message:
          payload?.message ||
          (valid
            ? 'Chain verified.'
            : brokenAt
            ? `Chain broken at sequence ${brokenAt}.`
            : 'Chain invalid.'),
        missingRpc: false,
      }
      setState(next)
      setChainContext(next)
      setAuthRequired(false)
      recordHookOutcome('useChainIntegrity', 'live_success', {
        rpc: 'olv_verify_event_chain',
        durationMs,
        message: valid ? 'verified' : 'invalid',
      })
    } catch (err) {
      const next: ChainState = {
        loading: false,
        valid: false,
        checked: eventCount,
        message: 'Awaiting chain verification service',
        missingRpc: true,
      }
      setState(next)
      setChainContext(next)
      captureHookFailure('useChainIntegrity', 'live_error', err, {
        rpc: 'olv_verify_event_chain',
      })
    }
  }, [eventCount, demoTamper])

  useEffect(() => {
    verify()
  }, [verify])

  return {
    ...state,
    authRequired,
    demoTamper,
    setDemoTamper,
    refresh: verify,
  }
}
